import React, { forwardRef } from "react";
import { styled } from "@mui/material/styles";
import { InputAdornment, Typography } from "@mui/material";
import MuiButton, { ButtonProps } from "@mui/material/Button";
import Grid, { GridProps } from "@mui/material/Grid";
import FormHelperText, { FormHelperTextProps } from "@mui/material/FormHelperText";
import OutlinedInput, { OutlinedInputProps } from "@mui/material/OutlinedInput";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import MuiCard, { CardProps } from "@mui/material/Card";
import MuiPaper, { PaperProps } from "@mui/material/Paper";
import Select, { SelectProps } from "@mui/material/Select";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import colors from "./colors";

export const AlertError = styled(
  forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
    <MuiAlert {...props} ref={ref} severity="error" data-testid="alert-error-container" />
  ))
)({
  marginTop: "1rem",
  "&.MuiPaper-root": {
    color: colors.darkRed1,
    backgroundColor: colors.lightRed1
  }
});

export const AlertSuccess = styled(
  forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
    <MuiAlert {...props} ref={ref} severity="success" color="success" variant="filled" data-testid="alert-success-container" />
  ))
)({
  "&.MuiPaper-root": {
    width: "100%",
    color: colors.white,
    backgroundColor: colors.successColor
  }
});

export const AlertInfo = styled(
  forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
    <MuiAlert {...props} ref={ref} severity="info" data-testid="alert-info-container" />
  ))
)({
  "&.MuiPaper-root": {
    marginTop: "2rem",
    color: colors.white,
    backgroundColor: colors.primary,
    "& svg": {
      fill: colors.white
    },
    "& .alert-info-link": {
      cursor: "pointer",
      textDecoration: "underline"
    }
  }
});

export const AlertWarning = styled(
  forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
    // TODO: To figure out how to put the custom icon into the alert when more time is available
    <MuiAlert {...props} ref={ref} severity="warning" data-testid="alert-warning-container" />
  ))
)({
  "&.MuiPaper-root": {
    marginTop: "2rem",
    marginBottom: "2rem",
    color: colors.yellow[800],
    backgroundColor: colors.yellow[100],
    "& .MuiAlert-icon": {
      alignItems: "center",
      justifyContent: "center"
    },
    "& svg": {
      fill: colors.yellow[800]
    },
    "& .alert-info-link": {
      cursor: "pointer",
      textDecoration: "underline"
    }
  }
});

export const CardOutlined = styled((props: CardProps) => <MuiCard {...props} variant="outlined" />)({
  "&.MuiPaper-outlined": {
    color: colors.darkGrey1,
    backgroundColor: colors.white,
    border: `1px solid ${colors.lightGrey3}`,
    borderRadius: "6px"
  }
});

export const InputLabel = styled((props: { children?: React.ReactNode }) => <Typography {...props} variant="subtitle2" />)({
  wordBreak: "break-word",
  color: colors.darkGrey1,
  paddingBottom: "8px",
  "& [data-testid^='optional']": {
    fontWeight: 600,
    color: colors.lightGrey5
  }
});

export const InputBase = styled((props: OutlinedInputProps) => <OutlinedInput {...props} fullWidth />)({
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
    borderRadius: 4,
    padding: "5px 12px",
    border: `1px solid ${colors.lightGrey4}`,
    "&:focus": {
      borderColor: colors.blueColor1
    },
    "&.Mui-disabled": {
      WebkitTextFillColor: colors.darkGrey1,
      backgroundColor: colors.lightGrey1
    }
  }
});

export const CompanyNameDashedInput = styled((props: OutlinedInputProps) => <OutlinedInput {...props} fullWidth />)({
  "& .MuiOutlinedInput-input": {
    fontSize: 14,
    borderRadius: 4,
    padding: "5px 32px",
    border: `1px dashed ${colors.green["500"]}`,
    "&:focus": {
      borderColor: colors.blueColor1
    },
    "&.Mui-disabled": {
      WebkitTextFillColor: colors.darkGrey1
    },
    "& + .MuiOutlinedInput-notchedOutline": {
      border: `none`
    }
  }
});

export const NativeSelect = styled((props: SelectProps<string>) => <Select {...props} native fullWidth variant="outlined" />)({
  select: {
    "&.MuiOutlinedInput-input": {
      fontSize: 14,
      fontWeight: 400,
      textAlign: "left",
      padding: "3px 26px 7px 10px",
      backgroundColor: colors.white,
      border: `1px solid ${colors.lightGrey4}`,
      transition: "border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      "&:focus": {
        borderColor: colors.blueColor1
      },
      "&.Mui-disabled": {
        WebkitTextFillColor: colors.darkGrey1,
        backgroundColor: colors.lightGrey1
      }
    }
  },
  "& .MuiSvgIcon-root": {
    color: colors.primary
  }
});

export const FilterTextField = styled(TextField)({
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input": {
    paddingTop: 0
  },
  "& .MuiInputLabel-root": {
    marginTop: "-3px",
    fontSize: "14px"
  },
  "& .MuiInputLabel-root.MuiFormLabel-filled, & .MuiInputLabel-root.Mui-focused": {
    marginTop: "2px"
  },
  "& .MuiOutlinedInput-root": {
    fontSize: 14,
    fontWeight: 400,
    textAlign: "left",
    padding: "0",
    height: "32px",
    transition: "border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    backgroundColor: colors.white,
    "& fieldset": {
      height: "35px"
    }
  }
});

export const HelperText = styled((props: FormHelperTextProps) => <FormHelperText {...props} />)({
  wordBreak: "break-word",
  marginLeft: 0,
  marginRight: 0
});

export const GridContainer = styled((props: GridProps) => <Grid alignItems="center" spacing={2} {...props} container />)({
  justifyContent: "center"
});

export const Paper = styled((props: PaperProps) => <MuiPaper elevation={0} {...props} />)({
  "&.MuiPaper-rounded": {
    padding: 0,
    border: `1px solid ${colors.lightGrey3}`,
    backgroundColor: colors.lightGrey1,
    borderRadius: "6px",
    color: colors.darkGrey1
  }
});

export const Button = styled((props: ButtonProps) => <MuiButton disableRipple disableElevation {...props} />)(({ theme }) => ({
  padding: "4px 14px",
  width: "auto",
  [theme.breakpoints.down("sm")]: {
    width: "100%"
  },
  "& .MuiButton-startIcon": {
    marginRight: "5px"
  },
  "@media (max-width: 400px)": {
    marginRight: 0
  }
}));

export const HeadingGreen = styled((props: { children?: React.ReactNode }) => <Typography {...props} />)({
  fontSize: 13,
  fontWeight: 700,
  color: colors.greenColor2,
  textTransform: "uppercase",
  wordBreak: "break-word",
  marginTop: "40px",
  marginBottom: "2px"
});

export const Input = styled((props: TextFieldProps) => (
  <TextField
    fullWidth
    variant="outlined"
    {...props}
    InputProps={{
      size: "small",
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon color="primary" />
        </InputAdornment>
      ),
      ...props.InputProps
    }}
  />
))({
  "& .MuiOutlinedInput-root": {
    fontSize: 15,
    paddingRight: "8px",
    "&:hover": {
      backgroundColor: colors.lightGrey2
    }
  },
  input: {
    height: "22px",
    padding: "12px 4px",
    "&:placeholder": {
      fontSize: 15,
      color: colors.lightGrey8
    }
  },
  fieldset: {
    borderColor: colors.lightGrey4,
    borderRadius: "6px"
  },
  "& .MuiInputAdornment-positionEnd": {
    "& svg": {
      fontSize: "18px"
    }
  }
});
